/**
 * SnapshotPulseWidget (Iter 36.l)
 *
 * Cross-workspace bundle-snapshot activity rollup on the Workspaces hub.
 * Sibling of WeeklyDigestCard — same layout language, different domain
 * (steering bundles instead of heat-map shares).
 *
 * Three interaction points:
 *   1. Lookback presets (7d / 14d / 30d) — re-fetches the pulse.
 *   2. "Email me this pulse" → POST /api/snapshot-pulse/me/send (Resend
 *      stub mode just logs).
 *   3. "View..." links on each row → routes to the workspace dashboard.
 *
 * Empty state: shown when the user has zero active snapshots across all
 * workspaces. The component returns null in that case so the hub stays
 * clean for first-time users.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShareNetworkIcon as ShareNetwork,
  EnvelopeSimpleIcon as Envelope,
  EyeIcon as Eye,
  EyeSlashIcon as EyeSlash,
  ClockIcon as Clock,
  CircleNotchIcon as Spinner,
  ArrowsClockwiseIcon as ArrowsClockwise,
  CaretDownIcon as CaretDown,
  CaretUpIcon as CaretUp,
} from "@phosphor-icons/react";
import { getSnapshotPulse, sendSnapshotPulse } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

const LOOKBACK_PRESETS = [
  { days: 7,  label: "7d" },
  { days: 14, label: "14d" },
  { days: 30, label: "30d" },
];

function Kpi({ label, value, color, testId }) {
  return (
    <div className="ml-card p-3 text-center" data-testid={testId}>
      <div className="ml-mono text-[9px] uppercase tracking-widest text-[var(--ml-text-secondary)]">{label}</div>
      <div className="text-xl font-semibold mt-1" style={{ color }}>
        {(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

function RowTable({ rows, emptyMsg, testIdPrefix }) {
  if (!rows?.length) {
    return (
      <p className="text-xs text-[var(--ml-text-secondary)] italic mb-3" data-testid={`${testIdPrefix}-empty`}>
        {emptyMsg}
      </p>
    );
  }
  return (
    <div className="border border-[var(--ml-border)]" data-testid={testIdPrefix}>
      <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-[var(--ml-surface)]/40 ml-mono text-[9px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
        <div className="col-span-5">Workspace</div>
        <div className="col-span-4">Recipient</div>
        <div className="col-span-1 text-right">Views</div>
        <div className="col-span-2 text-right"></div>
      </div>
      {rows.map((r) => (
        <div
          key={r.token}
          className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-[var(--ml-border)] items-center text-sm"
          data-testid={`${testIdPrefix}-row-${r.token_suffix}`}
        >
          <div className="col-span-5 truncate">
            <div className="font-semibold truncate">{r.workspace_name}</div>
            {r.client_name && (
              <div className="ml-mono text-[10px] text-[var(--ml-text-secondary)] truncate">{r.client_name}</div>
            )}
          </div>
          <div className="col-span-4 text-xs text-[var(--ml-text-secondary)] truncate">
            {r.recipient_email || r.recipient_label || r.label || <em>no recipient</em>}
          </div>
          <div className="col-span-1 text-right ml-mono">{r.view_count}</div>
          <div className="col-span-2 text-right">
            <Link
              to={`/workspaces/${r.workspace_id}/metadata/dashboard`}
              className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline"
              data-testid={`${testIdPrefix}-view-${r.token_suffix}`}
            >
              Dashboard →
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SnapshotPulseWidget() {
  const [pulse, setPulse] = useState(null);
  const [lookbackDays, setLookbackDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    getSnapshotPulse(lookbackDays)
      .then(setPulse)
      .catch(() => setPulse(null))
      .finally(() => setLoading(false));
  }, [lookbackDays]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleSend = async () => {
    setSending(true);
    try {
      const r = await sendSnapshotPulse(lookbackDays);
      toast.success(
        r.stubbed
          ? "Pulse email logged (stub mode — no Resend key set)."
          : "Pulse email sent to your inbox.",
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not send pulse email.");
    } finally {
      setSending(false);
    }
  };

  if (loading && !pulse) return null;

  const t = pulse?.totals || {};
  const hasAnyActivity =
    (t.active_snapshots || 0) > 0 ||
    (t.snapshots_created_in_period || 0) > 0 ||
    (t.total_views_in_period || 0) > 0;

  // Hide entirely when the user has nothing — the iter 36.j empty state
  // on the workspace dashboard already nudges first-time snapshot minting.
  if (!hasAnyActivity) return null;

  return (
    <div className="ml-card p-5 mb-6" data-testid="snapshot-pulse-widget">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5">
            <ShareNetwork size={11} weight="bold" /> Snapshot pulse · {lookbackDays}-day window
          </div>
          <h2 className="text-lg font-semibold mt-1">Steering bundle activity</h2>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex gap-1 mr-1" role="radiogroup">
            {LOOKBACK_PRESETS.map((p) => (
              <button
                key={p.days}
                type="button"
                onClick={() => setLookbackDays(p.days)}
                data-testid={`snapshot-pulse-lookback-${p.days}`}
                className={`px-2 py-1 text-xs ml-mono border ${lookbackDays === p.days ? "bg-[var(--ml-primary)] text-white border-[var(--ml-primary)]" : "border-[var(--ml-border)] hover:bg-[var(--ml-surface)]/40"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={reload}
            disabled={loading}
            className="ml-btn-icon"
            data-testid="snapshot-pulse-refresh"
            title="Refresh"
          >
            {loading ? <Spinner size={13} className="animate-spin" /> : <ArrowsClockwise size={13} weight="bold" />}
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending}
            className="ml-btn-secondary text-xs"
            data-testid="snapshot-pulse-send-email"
          >
            <Envelope size={12} weight="bold" />
            {sending ? "Sending…" : "Email me"}
          </button>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="ml-btn-icon"
            data-testid="snapshot-pulse-collapse"
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? <CaretDown size={13} weight="bold" /> : <CaretUp size={13} weight="bold" />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="snapshot-pulse-kpis">
            <Kpi label="Active" value={t.active_snapshots} testId="snapshot-pulse-kpi-active" />
            <Kpi label="Created" value={t.snapshots_created_in_period} testId="snapshot-pulse-kpi-created" />
            <Kpi
              label="Views"
              value={t.total_views_in_period}
              color={(t.total_views_in_period || 0) > 0 ? "#0e6027" : undefined}
              testId="snapshot-pulse-kpi-views"
            />
            <Kpi
              label="PDF dl"
              value={t.pdf_downloads_in_period}
              testId="snapshot-pulse-kpi-pdf"
            />
            <Kpi
              label="Expiring ≤7d"
              value={t.expiring_within_7d}
              color={(t.expiring_within_7d || 0) > 0 ? "#b28600" : undefined}
              testId="snapshot-pulse-kpi-expiring"
            />
          </div>

          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-2 mt-4">
            <Eye size={10} weight="bold" /> Top opened snapshots
          </div>
          <RowTable
            rows={pulse?.top_snapshots || []}
            emptyMsg="No active snapshots yet — mint one from any workspace's Modernization dashboard."
            testIdPrefix="snapshot-pulse-top"
          />

          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-2 mt-5">
            <EyeSlash size={10} weight="bold" /> Still unopened
          </div>
          <RowTable
            rows={pulse?.unopened || []}
            emptyMsg="No unopened snapshots — every link you minted has been opened at least once."
            testIdPrefix="snapshot-pulse-unopened"
          />

          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5 mb-2 mt-5">
            <Clock size={10} weight="bold" /> Expiring within 7 days
          </div>
          <RowTable
            rows={pulse?.expiring_soon || []}
            emptyMsg="No snapshots expiring in the next 7 days."
            testIdPrefix="snapshot-pulse-expiring"
          />
        </>
      )}
    </div>
  );
}
